import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { POOL_SIZE, POOL_RADIUS, PADDLE_COUNT } from './constants';
import { SimulationConfig, Emitter, Point } from './types';

interface WavePoolProps {
  config: SimulationConfig;
  emitters: Emitter[];
  focusPoints?: Point[];
  resetTrigger: number;
}

const WavePool: React.FC<WavePoolProps> = ({ config, emitters, focusPoints, resetTrigger }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  
  // Physics buffers
  const buffer1Ref = useRef<Float32Array>(new Float32Array(POOL_SIZE * POOL_SIZE));
  const buffer2Ref = useRef<Float32Array>(new Float32Array(POOL_SIZE * POOL_SIZE));
  const imageDataRef = useRef<ImageData | null>(null);

  // Time step counter for strict synchronization
  const simulationStepRef = useRef(0);
  
  // Track when the current burst/sequence started
  const emissionStartStepRef = useRef(0);

  const isDragging = useRef(false);

  // Precompute paddle positions on the rim
  const paddles = useMemo(() => {
    const p = [];
    const cx = POOL_SIZE / 2;
    const cy = POOL_SIZE / 2;
    // We place paddles slightly outside the water radius so they push in
    const r = POOL_RADIUS; 
    
    for (let i = 0; i < PADDLE_COUNT; i++) {
        const theta = (i / PADDLE_COUNT) * Math.PI * 2;
        // Position on grid
        const x = cx + Math.cos(theta) * r;
        const y = cy + Math.sin(theta) * r;
        p.push({ x, y, theta, index: i });
    }
    return p;
  }, []);

  // Calculate Phased Array Phases
  const paddlePhases = useMemo(() => {
    if (!focusPoints || focusPoints.length === 0) return null;

    const phases = new Float32Array(PADDLE_COUNT);
    const omega = (config.baseFrequency ?? config.frequency);

    for (let i = 0; i < PADDLE_COUNT; i++) {
        const p = paddles[i];
        let minDist = Infinity;
        for(const fp of focusPoints) {
            const fx = fp.x * POOL_SIZE;
            const fy = fp.y * POOL_SIZE;
            const d = Math.sqrt((p.x - fx)**2 + (p.y - fy)**2);
            if(d < minDist) minDist = d;
        }
        phases[i] = minDist * omega;
    }
    return phases;
  }, [focusPoints, paddles, config.baseFrequency, config.frequency]);

  // Reset burst timer when patterns change
  useEffect(() => {
    emissionStartStepRef.current = simulationStepRef.current;
  }, [emitters, focusPoints, config.loop, config.baseFrequency, config.frequency]);

  const initPhysics = useCallback(() => {
    buffer1Ref.current.fill(0);
    buffer2Ref.current.fill(0);
    simulationStepRef.current = 0;
    emissionStartStepRef.current = 0;
    if (canvasRef.current) {
         // Clear visual artifacts if any
         const ctx = canvasRef.current.getContext('2d');
         if (ctx) {
             ctx.clearRect(0, 0, POOL_SIZE, POOL_SIZE);
             // Force a redraw of the static pool in the next frame
             imageDataRef.current = null;
         }
    }
  }, []);

  // Effect to handle manual reset
  useEffect(() => {
    initPhysics();
  }, [resetTrigger, initPhysics]);

  const processFluid = () => {
    const b1 = buffer1Ref.current;
    const b2 = buffer2Ref.current;
    const w = POOL_SIZE;
    const damping = config.damping;
    
    simulationStepRef.current += 1;
    const step = simulationStepRef.current;
    const omega = (config.baseFrequency ?? config.frequency);

    // Burst Logic: If loop is off, calculate how long we've been running this sequence
    const burstDuration = (2 * Math.PI / omega) * 5; // 5 full wave periods is usually enough for a shape to form and clear
    const elapsedSinceStart = step - emissionStartStepRef.current;
    const shouldEmit = config.loop || elapsedSinceStart < burstDuration;

    // 1. Handle Emitters
    if (shouldEmit) {
        if (paddlePhases) {
            for(let i=0; i < PADDLE_COUNT; i++) {
                const p = paddles[i];
                const phase = paddlePhases[i];
                const ix = Math.floor(p.x - Math.cos(p.theta) * 2);
                const iy = Math.floor(p.y - Math.sin(p.theta) * 2);
                const idx = iy * w + ix;
                if (idx >= 0 && idx < b1.length) {
                    b1[idx] = Math.sin(step * omega + phase) * config.amplitude * 100;
                }
            }
        } else {
            const time = step * omega; 
            emitters.forEach(e => {
                if (!e.active) return;
                const cx = Math.floor(e.x * w);
                const cy = Math.floor(e.y * w);
                const index = cy * w + cx;
                if (index >= 0 && index < b1.length) {
                    b1[index] = Math.sin(time + e.phase) * config.amplitude * 100;
                }
            });
        }
    }

    if (config.rainMode && Math.random() < 0.05) {
        const rx = Math.floor(Math.random() * (w - 2)) + 1;
        const ry = Math.floor(Math.random() * (w - 2)) + 1;
        b1[ry * w + rx] -= 100 * config.amplitude;
    }

    // 2. Wave Propagation
    for (let y = 1; y < w - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const i = y * w + x;
        const dx = x - w / 2;
        const dy = y - w / 2;
        const distSq = dx * dx + dy * dy;
        
        if (distSq > POOL_RADIUS * POOL_RADIUS) {
            b2[i] = 0; 
            continue;
        }

        const val = (b1[i - 1] + b1[i + 1] + b1[i - w] + b1[i + w]) / 2 - b2[i];
        b2[i] = val * damping;
      }
    }

    // Swap buffers
    const temp = buffer1Ref.current;
    buffer1Ref.current = buffer2Ref.current;
    buffer2Ref.current = temp;
  };

  const drawFluid = (ctx: CanvasRenderingContext2D) => {
    if (!imageDataRef.current) {
        imageDataRef.current = ctx.createImageData(POOL_SIZE, POOL_SIZE);
    }
    
    const imgData = imageDataRef.current;
    const data = imgData.data;
    const buffer = buffer1Ref.current;
    const w = POOL_SIZE;

    for (let i = 0; i < w * w; i++) {
        const x = i % w;
        const y = Math.floor(i / w);
        const dx = x - w / 2;
        const dy = y - w / 2;
        const distSq = dx * dx + dy * dy;

        if (distSq > POOL_RADIUS * POOL_RADIUS) {
            const p = i * 4;
            data[p] = 2; data[p+1] = 6; data[p+2] = 23; data[p+3] = 255;
            continue;
        }

        const val = buffer[i];
        
        const prevX = buffer[i-1] || val;
        const nextX = buffer[i+1] || val;
        const prevY = buffer[i-w] || val;
        const nextY = buffer[i+w] || val;

        const xGrad = nextX - prevX;
        const yGrad = nextY - prevY;

        let r = 0, g = 30, b = 60;
        const light = (xGrad + yGrad);
        
        r += light * 2 + val; 
        g += light * 2 + val + 20; 
        b += light * 4 + val + 80;

        r = Math.min(255, Math.max(0, r));
        g = Math.min(255, Math.max(0, g));
        b = Math.min(255, Math.max(0, b));

        const p = i * 4;
        data[p] = r; data[p+1] = g; data[p+2] = b; data[p+3] = 255; 
    }

    ctx.putImageData(imgData, 0, 0);

    const cx = POOL_SIZE / 2;
    const cy = POOL_SIZE / 2;
    ctx.strokeStyle = '#334155';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(cx, cy, POOL_RADIUS + 2, 0, Math.PI * 2);
    ctx.stroke();

    const step = simulationStepRef.current;
    const omega = (config.baseFrequency ?? config.frequency);

    // Burst visual check
    const burstDuration = (2 * Math.PI / omega) * 5;
    const elapsedSinceStart = step - emissionStartStepRef.current;
    const isEmitting = config.loop || elapsedSinceStart < burstDuration;

    paddles.forEach((p, i) => {
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.theta);

        let intensity = 0;
        let isActive = false;

        // Visuals should match physics emission state
        if (isEmitting) {
            if (paddlePhases) {
                isActive = true;
                const phase = paddlePhases[i];
                intensity = (Math.sin(step * omega + phase) + 1) / 2;
            } else {
                 // Check if manual emitters are affecting this area roughly? 
                 // For now just keep low ambient for manual mode or check if active
                 if (emitters.length > 0) intensity = 0.2;
            }
        }

        const barHeight = 6 + intensity * 6;
        const color = isActive 
          ? `rgba(100, 255, 218, ${0.4 + intensity * 0.6})` 
          : `rgba(148, 163, 184, 0.3)`;

        ctx.fillStyle = color;
        ctx.fillRect(-2, -barHeight/2, 4, barHeight);
        ctx.restore();
    });
  };

  const tick = useCallback(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    if (config.autoPlay) {
      processFluid();
    }
    drawFluid(ctx);

    animationRef.current = requestAnimationFrame(tick);
  }, [config, emitters, focusPoints, paddlePhases, paddles]);

  useEffect(() => {
    // Only start animation loop once
    animationRef.current = requestAnimationFrame(tick);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []); 

  // Re-bind tick when dependencies change
  useEffect(() => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      animationRef.current = requestAnimationFrame(tick);
  }, [tick]);

  const handleInteract = (e: React.MouseEvent | React.TouchEvent) => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    
    let clientX, clientY;
    if ('touches' in e) {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
    } else {
        clientX = (e as React.MouseEvent).clientX;
        clientY = (e as React.MouseEvent).clientY;
    }

    const scaleX = POOL_SIZE / rect.width;
    const scaleY = POOL_SIZE / rect.height;

    const x = Math.floor((clientX - rect.left) * scaleX);
    const y = Math.floor((clientY - rect.top) * scaleY);

    if (x > 1 && x < POOL_SIZE - 1 && y > 1 && y < POOL_SIZE - 1) {
        buffer1Ref.current[y * POOL_SIZE + x] -= 500 * config.amplitude;
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
      isDragging.current = true;
      handleInteract(e);
  };
  const handleMouseMove = (e: React.MouseEvent) => {
      if(isDragging.current) handleInteract(e);
  };
  const handleMouseUp = () => {
      isDragging.current = false;
  };

  return (
    <div className="relative group cursor-crosshair w-full flex justify-center">
       <canvas
        ref={canvasRef}
        width={POOL_SIZE}
        height={POOL_SIZE}
      className="w-full h-auto max-w-full aspect-square rounded-full shadow-[0_0_80px_rgba(100,255,218,0.05)] border-4 border-slate-800 bg-[#050b14]"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleInteract}
        onTouchMove={handleInteract}
      />
    </div>
  );
};

export default WavePool;
